// rumTracking.ts
import { datadogRum } from '@datadog/browser-rum'
import type { ContextValue } from '@datadog/browser-rum'

/**
 * Generic metadata type for RUM events
 */
export type RumContext = Record<string, ContextValue>

/**
 * Safe wrapper to avoid runtime crashes
 */
const isRumAvailable = (): boolean => globalThis.window !== undefined && !!datadogRum?.addAction

/**
 * Track user actions (clicks, submits, feature usage)
 */
export const trackAction = (actionName: string, context?: RumContext): void => {
    if (!isRumAvailable()) return

    datadogRum.addAction(actionName, context)
}

/**
 * Track handled / custom errors
 */
export const trackError = (error: Error, context?: RumContext): void => {
    if (!isRumAvailable()) return
    datadogRum.addError(error, context)
}

/**
 * Track identified users
 */
export const setRumUser = (user: { id: string; email?: string; name?: string; role?: string; zones?: string[]; business_functions?: string[]; countries?: string[]; method?: string }): void => {
    if (!isRumAvailable()) return

    datadogRum.setUser(user)
}

/**
 * Clear user data on logout
 */
export const clearRumUser = (): void => {
    if (!isRumAvailable()) return

    datadogRum.clearUser()
}

/**
 * Clear all global context
 */
export const clearGlobalContext = (): void => {
    if (!isRumAvailable()) return

    datadogRum.clearGlobalContext()
}

/**
 * Set the entire global context object — tags ALL subsequent events
 * Use on flow entry (e.g. wizard opened) to stamp experiment_id, step, type
 */
export const setGlobalContext = (context: RumContext): void => {
    if (!isRumAvailable()) return

    datadogRum.setGlobalContext(context)
}

/**
 * Update a single global context property without replacing the whole object
 * Use for incremental updates (e.g. step progression in wizard)
 */
export const setGlobalContextProperty = (key: string, value: unknown): void => {
    if (!isRumAvailable()) return

    datadogRum.setGlobalContextProperty(key, value)
}

/**
 * Remove a single global context property
 * Use when leaving a flow so stale keys don't leak into unrelated events
 */
export const removeGlobalContextProperty = (key: string): void => {
    if (!isRumAvailable()) return

    datadogRum.removeGlobalContextProperty(key)
}

/**
 * Update a single user property without resetting the full user object
 * Use for progressive enrichment (e.g. adding role after it's fetched)
 */
export const setUserProperty = (key: string, value: unknown): void => {
    if (!isRumAvailable()) return

    datadogRum.setUserProperty(key, value)
}

/**
 * Mark a named timing milestone on the current RUM View
 * Enables P50/P95/P99 percentile queries in Datadog for one-shot milestones
 */
export const addViewTiming = (name: string): void => {
    if (!isRumAvailable()) return

    datadogRum.addTiming(name)
}

/**
 * Override the auto-detected route name with a meaningful view name
 * Use on key pages: 'My Experiments', 'Executive Dashboard', 'Wizard Step 2', etc.
 */
export const startNamedView = (name: string): void => {
    if (!isRumAvailable()) return

    datadogRum.startView({ name })
}

/**
 * Get the internal RUM context (session_id, view.id, action.id)
 * Use for RUM ↔ log stitching — attach to custom log entries for correlation
 */
export const getRumContext = (): ReturnType<typeof datadogRum.getInternalContext> | undefined => {
    if (!isRumAvailable()) return undefined

    return datadogRum.getInternalContext()
}

/**
 * Stop session replay recording — use on sensitive pages (admin, PII-heavy)
 */
export const stopReplay = (): void => {
    if (!isRumAvailable()) return

    datadogRum.stopSessionReplayRecording()
}

/**
 * Resume session replay recording after leaving sensitive pages
 */
export const startReplay = (): void => {
    if (!isRumAvailable()) return

    datadogRum.startSessionReplayRecording()
}

/**
 * Measure performance of a custom flow
 */
export const startTiming = (name: string): void => {
    if (!isRumAvailable()) return

    performance.mark(`${name}_start`)
}

export const stopTiming = (name: string, context?: RumContext): void => {
    if (!isRumAvailable()) return

    performance.mark(`${name}_end`)
    performance.measure(name, `${name}_start`, `${name}_end`)

    const entries = performance.getEntriesByName(name)
    const duration = entries.at(-1)?.duration

    datadogRum.addAction(name, {
        ...context,
        durationMs: Math.round(duration ?? 0),
    })
}
